/**
 * Contract List Component
 * Task 43: Display contracts with active/expired filters and recurrence indicators
 */

import { useState, useMemo } from 'react';
import { IndustryContract } from '@/types/industry';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MoreHorizontal, Pencil, Trash2, Plus, Search, FileText, RefreshCw, Calendar } from 'lucide-react';
import { format, parseISO, isAfter, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface ContractListProps {
  contracts: IndustryContract[];
  onEdit?: (contract: IndustryContract) => void;
  onDelete?: (contract: IndustryContract) => void;
  onCreate?: () => void;
}

type StatusFilter = 'all' | 'active' | 'expired';
type RecurrenceType = 'monthly' | 'quarterly' | 'semiannual' | 'annual' | 'custom' | null;

const RECURRENCE_LABELS: Record<string, string> = {
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
  annual: 'Anual',
  custom: 'Personalizado',
};

export function ContractList({ contracts, onEdit, onDelete, onCreate }: ContractListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Determine contract status
  const getContractStatus = (contract: IndustryContract): 'active' | 'expired' => {
    if (!contract.end_date) return 'active';

    const endDate = parseISO(contract.end_date);
    const now = new Date();

    return isAfter(endDate, now) ? 'active' : 'expired';
  };

  // Filter contracts
  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesClient = contract.metadata?.clientName?.toString().toLowerCase().includes(term);
        const matchesDescription = contract.metadata?.description?.toString().toLowerCase().includes(term);
        if (!matchesClient && !matchesDescription) return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        const status = getContractStatus(contract);
        if (status !== statusFilter) return false;
      }

      return true;
    });
  }, [contracts, searchTerm, statusFilter]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return format(parseISO(dateString), 'dd/MM/yyyy', { locale: ptBR });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Contratos</CardTitle>
              <CardDescription>Gerencie contratos e recorrências</CardDescription>
            </div>
            {onCreate && (
              <Button onClick={onCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Contrato
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar contratos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(value: StatusFilter) => setStatusFilter(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Contratos</SelectItem>
                <SelectItem value="active">Apenas Ativos</SelectItem>
                <SelectItem value="expired">Apenas Expirados</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-muted-foreground">
            Mostrando {filteredContracts.length} de {contracts.length} contratos
          </div>
        </CardContent>
      </Card>

      {/* Contracts Table */}
      <Card>
        <CardContent className="pt-6">
          {filteredContracts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchTerm || statusFilter !== 'all'
                ? 'Nenhum contrato encontrado com os filtros selecionados'
                : 'Nenhum contrato cadastrado'}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente/Projeto</TableHead>
                    <TableHead>Vigência</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Recorrência</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContracts.map((contract) => {
                    const status = getContractStatus(contract);
                    const recurrence = contract.metadata?.recurrence as RecurrenceType;

                    return (
                      <TableRow key={contract.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              {contract.metadata?.clientName || contract.metadata?.projectName || 'Sem nome'}
                            </div>
                            {contract.metadata?.description && (
                              <div className="text-sm text-muted-foreground line-clamp-1">
                                {contract.metadata.description as string}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Calendar className="h-3 w-3" />
                            {formatDate(contract.start_date)} - {formatDate(contract.end_date)}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(contract.value)}
                          {recurrence && (
                            <div className="text-xs text-muted-foreground">
                              /{RECURRENCE_LABELS[recurrence]?.toLowerCase() || recurrence}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {recurrence ? (
                            <Badge variant="outline" className="flex items-center gap-1 w-fit">
                              <RefreshCw className="h-3 w-3" />
                              {RECURRENCE_LABELS[recurrence] || recurrence}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">Único</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={status === 'active' ? 'default' : 'secondary'}>
                            {status === 'active' ? 'Ativo' : 'Expirado'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {onEdit && (
                                <DropdownMenuItem onClick={() => onEdit(contract)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Editar
                                </DropdownMenuItem>
                              )}
                              {onDelete && (
                                <DropdownMenuItem
                                  onClick={() => onDelete(contract)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
